/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Search as SearchIcon
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import { toast } from 'react-toastify';
import { formatHoursLabel, formatHoursConversion } from '../../utils/timeConversion';

const LightingManagement = () => {
  const [lighting, setLighting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLighting, setEditingLighting] = useState(null);
  const [newLighting, setNewLighting] = useState({
    light_type: '',
    description: '',
    life_expectancy: ''
  });

  // Pagination and search states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredLighting, setFilteredLighting] = useState([]);

  // Sorting hook
  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredLighting,
    { defaultSortColumn: 'light_type', defaultSortOrder: 'asc' }
  );

  const fetchData = useCallback(async () => {
    try {
      const lightingRes = await axios.get('/api/lighting');
      setLighting(lightingRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const searchLower = searchQuery.toLowerCase();
    const filtered = lighting.filter(item =>
      item.light_type?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      String(item.life_expectancy ?? '').toLowerCase().includes(searchLower)
    );
    setFilteredLighting(filtered);
    setPage(0);
  }, [searchQuery, lighting]);

  // Pagination handlers
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate pagination values
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedLighting = sortedData.slice(startIndex, endIndex);

  // CRUD operations
  const handleCreate = async () => {
    try {
      if (!newLighting.light_type) {
        alert('Please provide a light type');
        return;
      }

      await axios.post('/api/lighting', newLighting);
      setNewLighting({ light_type: '', description: '', life_expectancy: '' });
      fetchData();
      toast.success('Light type created successfully');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error creating light type');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/api/lighting/${editingLighting._id}`, {
        light_type: editingLighting.light_type,
        description: editingLighting.description,
        life_expectancy: editingLighting.life_expectancy
      });
      setEditDialogOpen(false);
      setEditingLighting(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Error updating light type');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this light type?')) {
      try {
        await axios.delete(`/api/lighting/${id}`);
        fetchData();
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete light type');
      }
    }
  };

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
      <DialogTitle>Add New Light Type</DialogTitle>
      <DialogContent>
        <TextField
          label="Light Type"
          fullWidth
          margin="normal"
          placeholder="e.g., Type A, LED 600W"
          value={newLighting.light_type}
          onChange={(e) => setNewLighting({ ...newLighting, light_type: e.target.value })}
        />
        <TextField
          label="Description (optional)"
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          value={newLighting.description}
          onChange={(e) => setNewLighting({ ...newLighting, description: e.target.value })}
        />
        <TextField
          label="Life Expectancy in hours (optional)"
          type="number"
          fullWidth
          margin="normal"
          placeholder="e.g., 10000 or 100.5"
          inputProps={{ min: 0, step: 'any' }}
          helperText={
            newLighting.life_expectancy !== '' && newLighting.life_expectancy != null
              ? formatHoursConversion(newLighting.life_expectancy)
              : 'Enter the number of hours (decimals allowed)'
          }
          value={newLighting.life_expectancy}
          onChange={(e) => setNewLighting({ ...newLighting, life_expectancy: e.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
      <DialogTitle>Edit Light Type</DialogTitle>
      <DialogContent>
        <TextField
          label="Light Type"
          fullWidth
          margin="normal"
          value={editingLighting?.light_type || ''}
          onChange={(e) => setEditingLighting({ ...editingLighting, light_type: e.target.value })}
        />
        <TextField
          label="Description (optional)"
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          value={editingLighting?.description || ''}
          onChange={(e) => setEditingLighting({ ...editingLighting, description: e.target.value })}
        />
        <TextField
          label="Life Expectancy in hours (optional)"
          type="number"
          fullWidth
          margin="normal"
          placeholder="e.g., 10000 or 100.5"
          inputProps={{ min: 0, step: 'any' }}
          helperText={
            editingLighting?.life_expectancy !== '' && editingLighting?.life_expectancy != null
              ? formatHoursConversion(editingLighting?.life_expectancy)
              : 'Enter the number of hours (decimals allowed)'
          }
          value={editingLighting?.life_expectancy ?? ''}
          onChange={(e) => setEditingLighting({ ...editingLighting, life_expectancy: e.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdate} variant="contained" color="primary">
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select
          value={rowsPerPage}
          onChange={handleChangeRowsPerPage}
          sx={{ mx: 1 }}
          size="small"
        >
          {[10, 25, 50, 100].map(n => (
            <MenuItem key={n} value={n}>{n}</MenuItem>
          ))}
        </Select>
        <span>entries</span>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page" onClick={() => handleChangePage(0)} disabled={page === 0}>
          <FirstPage />
        </IconButton>
        <IconButton aria-label="Previous page" onClick={() => handleChangePage(page - 1)} disabled={page === 0}>
          <KeyboardArrowLeft />
        </IconButton>
        <Box sx={{ mx: 2 }}>
          Page {page + 1} of {Math.max(1, Math.ceil(filteredLighting.length / rowsPerPage))}
        </Box>
        <IconButton aria-label="Next page"
          onClick={() => handleChangePage(page + 1)}
          disabled={page >= Math.ceil(filteredLighting.length / rowsPerPage) - 1}
        >
          <KeyboardArrowRight />
        </IconButton>
        <IconButton aria-label="Last page"
          onClick={() => handleChangePage(Math.ceil(filteredLighting.length / rowsPerPage) - 1)}
          disabled={page >= Math.ceil(filteredLighting.length / rowsPerPage) - 1}
        >
          <LastPage />
        </IconButton>
      </Box>
    </Box>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="lighting-management">
      <h2>Lighting Management</h2>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search light types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
        >
          Add New Light Type
        </Button>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <SortableTableHeader column="light_type" label="Light Type" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="description" label="Description" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="life_expectancy" label="Life Expectancy (hours)" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedLighting.map(item => (
            <tr key={item._id}>
              <td><strong>{item.light_type}</strong></td>
              <td>{item.description || <em style={{ color: '#999' }}>—</em>}</td>
              <td>
                {item.life_expectancy != null && item.life_expectancy !== '' ? (
                  <Tooltip title={formatHoursConversion(item.life_expectancy)} arrow>
                    <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
                      {formatHoursLabel(item.life_expectancy)}
                    </span>
                  </Tooltip>
                ) : (
                  <em style={{ color: '#999' }}>—</em>
                )}
              </td>
              <td className="action-buttons">
                <Button
                  variant="contained"
                  size="small"
                  className="row-action-edit"
                  style={{ marginRight: '8px' }}
                  onClick={() => {
                    setEditingLighting(item);
                    setEditDialogOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => handleDelete(item._id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {renderPaginationControls()}

      <Box sx={{ mt: 2, color: 'text.secondary' }}>
        Showing {Math.min(startIndex + 1, sortedData.length)} to {endIndex} of {sortedData.length} entries
        {searchQuery && ` (filtered from ${lighting.length} total entries)`}
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
    </div>
  );
};

export default LightingManagement;
